<script lang="ts">
export default {
    name: 'HeaderContent',
}
</script>

<script setup lang="ts">
// @ts-nocheck
import { ref, computed, onMounted, watch, unref } from 'vue'
import { storeToRefs } from 'pinia'
import { Container, Button, Dropdown, Card, ButtonGroup } from '@inhaus-ai-limited/vue-components'
import router from '@/router'
import HeroIcon from '@/components/HeroIcon.vue'
import InviteModel from '@/components/InviteModel.vue'
import { useRoute } from 'vue-router'
import { Routes } from '@/router/Routes'
import getCurrentRouteName from '@/utils/getRouteName'
import { useOrganizationStore, type OrganizationSummary } from '../../stores/organization'
import { useRefStore } from '@/stores/refStore'
import { useStytchStore } from '@/stores/stytchStore'

const stytch = useStytchStore()
// Use storeToRefs so we get stable reactive refs from the Pinia store
const { user: stytchUser, isAuthenticated: stytchIsAuthenticated } = storeToRefs(stytch)

// Use only Stytch for authentication
const user = computed(() => unref(stytchUser))
const isAuthenticated = computed(() => !!stytchIsAuthenticated.value)
const isAnyAuthenticated = computed(() => isAuthenticated.value)
const combinedUser = computed(() => stytchUser.value)

const refStore = useRefStore()
const organizationStore = useOrganizationStore()
const route = useRoute()

const selectedTeam = JSON.parse(localStorage.getItem('selectedTeam') || '{}')
const orgInviteModel = ref<typeof InviteModel>()
const teamOptions = ref<{ label: string; value: string }[]>([])

const login = () => router.push('/auth/signin')
const logout = () => {
    refStore.reset()
    localStorage.removeItem('selectedTeam')
    stytch.logout()

    router.push('/auth/signin')
}

// Dev-only helper to toggle Stytch auth for deterministic testing
const toggleStychDebug = () => {
    try {
        // @ts-ignore
        const current = !!stytchIsAuthenticated.value
        // call the store debug action
        // @ts-ignore
        stytch.debugSetAuthenticated(!current, !current ? { email: 'dev+stytch@example.com' } : null)
    } catch (e) {
        // eslint-disable-next-line no-console
        console.warn('toggleStychDebug failed', e)
    }
}

// Expose dev mode flag to template (avoid using import.meta in template expressions)
const isDev = import.meta.env.MODE !== 'production'

const navigateToOverViewPage = () => {
    localStorage.removeItem('selectedTeam')
    router.push('/super-admin')
}

watch(
    () => organizationStore.organizations,
    (orgs: OrganizationSummary[]) => {
        teamOptions.value = orgs.map((organization) => ({
            label:
                organization.name ||
                organization.slug ||
                organization.raw?.organization_name ||
                organization.raw?.display_name ||
                organization.id,
            value: organization.id,
        }))
    },
    { immediate: true }
)

onMounted(async () => {
    // await organizationStore.fetchOrganizations();
    // optionally log current Stytch session for debugging; the SDK exposes .session.getSync()
    try {
        // not all environments expose getSync; guard it
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        const sess = stytch.stytch?.session?.getSync ? await stytch.stytch.session.authenticate() : null
        console.log('stytch session', sess, stytch)
    } catch (e) {
        // ignore
    }
})
</script>

<template>
    <InviteModel ref="orgInviteModel" />
    <Container class="grid grid-cols-3">
        <Container class="flex justify-start">
            <div v-if="isAnyAuthenticated" class="flex items-center gap-[20px]">
                <Button
                    @click="() => navigateToOverViewPage()"
                    :disabled="selectedTeam.id ? false : true"
                    class="!w-[80px]"
                    >Select Teams</Button
                >
                <span class="ml-2 text-gray-400 font-semibold w-[200px]">{{ selectedTeam.display_name }}</span>
            </div>
        </Container>
        <Container class="flex flex-col justify-center mx-auto">
            <Button
                variant="label"
                color="secondary"
                class="flex-auto justify-center items-center"
                @click="
                    router.push({
                        name: Routes.Home,
                        force: true,
                    })
                "
            >
                <img src="@/assets/logo.png" alt="InHaus.ai" class="w-94 h-25 mx-auto" />
            </Button>
        </Container>
        <Container class="flex flex-col justify-center">
            <!-- <div v-if="!isAnyAuthenticated" class="grid my-auto gap-[25px] justify-end h-fit">
                <ButtonGroup class="flex my-auto justify-end h-fit space-x-[5px]">
                    <Button
                        class="w-[100px] max-w-[120px] border-white border-[1px]"
                        variant="default"
                        size="md"
                        color="primary"
                        @click="login()"
                        >Log-in</Button
                    >
                </ButtonGroup>
            </div> -->
            <div v-if="isAnyAuthenticated" class="grid my-auto gap-[25px] justify-end h-fit">
                <!-- Dev-only Stytch auth toggle -->

                <Dropdown :fixed="true" class="min-w-[500px] [&>.absolute]:top-[50px] text-slate-400">
                    <template #trigger="{ toggle, isVisible }">
                        <Button size="lg" color="secondary" @click="toggle" class="space-x-10 !justify-end flex">
                            <span class="ml-2 text-gray-400 font-semibold">{{ combinedUser?.email }}</span>
                            <span class="ml-2 font-semibold w-[80px] text-end" :class="'text-black'">{{
                                selectedTeam.id ? getCurrentRouteName() : 'Menu'
                            }}</span>
                            <HeroIcon
                                v-if="isVisible"
                                icon="ChevronUpIcon"
                                class="w-20 h-20 rounded-sm inline-block float-right"
                            />
                            <HeroIcon
                                v-else
                                icon="ChevronDownIcon"
                                class="w-20 h-20 rounded-sm inline-block float-right"
                            />
                        </Button>
                    </template>
                    <Card :square="true" :shadow="true" class="max-w-[200px] float-right">
                        <ul class="flex flex-col items pt-10">
                            <li v-show="selectedTeam.id">
                                <Button
                                    textPosition="left"
                                    size="lg"
                                    color="secondary"
                                    :class="
                                        route.name == Routes.Projects
                                            ? 'text-left text-black'
                                            : 'text-left text-gray-400'
                                    "
                                    @click="router.push({ name: Routes.Projects })"
                                >
                                    <HeroIcon icon="HomeIcon" class="w-25 h-25 inline-block" />
                                    <span class="ml-10 font-medium">Projects</span>
                                </Button>
                            </li>
                            <li v-show="selectedTeam.id">
                                <Button
                                    textPosition="left"
                                    size="lg"
                                    color="secondary"
                                    :class="
                                        route.name == Routes.AdminJobs
                                            ? 'text-left text-black'
                                            : 'text-left text-gray-400'
                                    "
                                    @click="router.push({ name: Routes.AdminJobs })"
                                >
                                    <HeroIcon icon="BriefcaseIcon" class="w-25 h-25 inline-block" />
                                    <span class="ml-10 font-medium">Jobs</span>
                                </Button>
                            </li>
                            <li v-show="selectedTeam.id">
                                <Button
                                    textPosition="left"
                                    size="lg"
                                    color="secondary"
                                    :class="
                                        route.name == Routes.AdminDashboard
                                            ? 'text-left text-black'
                                            : 'text-left text-gray-400'
                                    "
                                    @click="router.push({ name: Routes.AdminDashboard })"
                                >
                                    <HeroIcon icon="HomeIcon" class="w-25 h-25 inline-block" />
                                    <span class="ml-10 font-medium">Batch</span>
                                </Button>
                            </li>
                            <li v-show="selectedTeam.id">
                                <Button
                                    textPosition="left"
                                    size="lg"
                                    color="secondary"
                                    :class="
                                        route.name == Routes.AdminSettings
                                            ? 'text-left text-black'
                                            : 'text-left text-gray-400'
                                    "
                                    @click="router.push({ name: Routes.AdminSettings })"
                                >
                                    <HeroIcon icon="CogIcon" class="w-25 h-25 inline-block" />
                                    <span class="ml-10 font-medium">Settings</span>
                                </Button>
                            </li>
                            <li v-show="selectedTeam.id">
                                <Button
                                    textPosition="left"
                                    size="lg"
                                    color="secondary"
                                    :class="
                                        route.name == Routes.AdminReports
                                            ? 'text-left text-black'
                                            : 'text-left text-gray-400'
                                    "
                                    @click="router.push({ name: Routes.AdminReports })"
                                >
                                    <HeroIcon icon="DocumentTextIcon" class="w-25 h-25 inline-block" />
                                    <span class="ml-10 font-medium">Reports</span>
                                </Button>
                            </li>
                            <li v-show="selectedTeam.id">
                                <Button
                                    textPosition="left"
                                    size="lg"
                                    color="secondary"
                                    :class="
                                        route.name == Routes.Credits
                                            ? 'text-left text-black'
                                            : 'text-left text-gray-400'
                                    "
                                    @click="router.push({ name: Routes.Credits })"
                                >
                                    <HeroIcon icon="DocumentTextIcon" class="w-25 h-25 inline-block" />
                                    <span class="ml-10 font-medium">Credits</span>
                                </Button>
                            </li>
                            <li>
                                <Button
                                    textPosition="left"
                                    size="lg"
                                    color="secondary"
                                    class="text-left text-gray-400"
                                    @click="logout"
                                >
                                    <HeroIcon icon="LogoutIcon" class="w-25 h-25 inline-block" />
                                    <span class="ml-10 font-medium">Logout</span>
                                </Button>
                            </li>
                        </ul>
                    </Card>
                </Dropdown>
            </div>
        </Container>
    </Container>
</template>
